import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Palette, Check, Loader2, Sun, Moon } from "lucide-react";
import { toast } from "sonner";

/** Compact mini-mockup of a page rendered in a given palette. */
function MiniPreview({ palette, accent, isLight }) {
  const textColor = isLight ? "#0A0A0A" : "#FFFFFF";
  return (
    <div style={{ backgroundColor: palette.bg }} className="p-2.5">
      <div style={{ backgroundColor: palette.surface, borderColor: palette.border }} className="rounded-sm border p-2">
        <div className="flex items-center gap-1.5 mb-2">
          <div style={{ backgroundColor: accent }} className="h-2 w-2 rounded-full" />
          <div style={{ backgroundColor: textColor, opacity: 0.85 }} className="h-1.5 w-10 rounded" />
        </div>
        <div style={{ backgroundColor: palette.surface2, borderColor: palette.border }} className="rounded-sm border p-1.5 mb-2">
          <div className="h-1 w-3/4 rounded mb-1" style={{ backgroundColor: palette.border }} />
          <div className="h-1 w-1/2 rounded" style={{ backgroundColor: palette.border }} />
        </div>
        <div className="flex items-center gap-1.5">
          <div style={{ backgroundColor: accent }} className="h-4 flex-1 rounded-sm flex items-center justify-center">
            <div className="h-1 w-6 rounded bg-white/80" />
          </div>
          <div style={{ borderColor: accent, color: accent }} className="h-4 w-6 rounded-sm border flex items-center justify-center text-[6px] font-bold">•••</div>
        </div>
      </div>
    </div>
  );
}

/** One scheme = a Dark + Light preview pair. Click either to apply that scheme in that mode. */
function SchemeCard({ t, activeId, mode, onApply, savingKey }) {
  const isActiveScheme = t.id === activeId;
  const modes = [
    { key: "dark", label: "Dark", Icon: Moon, palette: t.dark, isLight: false },
    { key: "light", label: "Light", Icon: Sun, palette: t.light, isLight: true },
  ];
  return (
    <div
      data-testid={`scheme-card-${t.id}`}
      className={`rounded-md overflow-hidden border-2 transition-all ${isActiveScheme ? "border-white" : "border-[#27272A] hover:border-zinc-600"}`}
    >
      <div className="grid grid-cols-2 gap-px bg-[#27272A]">
        {modes.map((m) => {
          const live = isActiveScheme && mode === m.key;
          const busy = savingKey === `${t.id}:${m.key}`;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onApply(t, m.key)}
              disabled={!!savingKey}
              data-testid={`theme-${t.id}-${m.key}`}
              title={`Apply ${t.name} · ${m.label}`}
              className={`relative bg-[#0A0A0A] text-left transition-all ${live ? "ring-2 ring-inset ring-emerald-400" : "hover:opacity-90"}`}
            >
              <div className="flex items-center justify-between px-2 pt-2">
                <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-zinc-400">
                  <m.Icon className="h-3 w-3" /> {m.label}
                </span>
                {live && (
                  <span className="text-[8px] uppercase tracking-widest font-bold text-emerald-400 flex items-center gap-0.5">
                    <Check className="h-2.5 w-2.5" /> Live
                  </span>
                )}
              </div>
              <MiniPreview palette={m.palette} accent={t.accent} isLight={m.isLight} />
              {busy && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between px-3 py-2 bg-[#0A0A0A] border-t border-[#27272A]">
        <div className="flex items-center gap-2">
          <span style={{ backgroundColor: t.accent }} className="h-3 w-3 rounded-full ring-1 ring-white/10" />
          <span className="text-sm text-white font-bold">{t.name}</span>
        </div>
        {isActiveScheme && (
          <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Active</span>
        )}
      </div>
    </div>
  );
}

export default function Appearance() {
  const [themes, setThemes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [mode, setMode] = useState("dark");
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null); // `${themeId}:${mode}`
  const [savingMode, setSavingMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/themes");
      setThemes(data.themes || []);
      setActiveId(data.active_id);
      setMode(data.mode || "dark");
    } catch { toast.error("Failed to load themes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isLight = mode === "light";

  const applyScheme = async (t, targetMode) => {
    if (t.id === activeId && targetMode === mode) return;
    const key = `${t.id}:${targetMode}`;
    setSavingKey(key);
    try {
      await api.put("/admin/theme", { theme_id: t.id, mode: targetMode });
      setActiveId(t.id);
      setMode(targetMode);
      toast.success(`${t.name} · ${targetMode === "light" ? "Light" : "Dark"} is now live`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingKey(null); }
  };

  const setThemeMode = async (nextMode) => {
    if (nextMode === mode || savingMode) return;
    setSavingMode(true);
    try {
      await api.put("/admin/theme", { mode: nextMode });
      setMode(nextMode);
      toast.success(`${nextMode === "light" ? "Light" : "Dark"} mode is now live`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingMode(false); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="admin-appearance-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Branding</p>
        <h1 className="font-display text-3xl text-white uppercase tracking-tight flex items-center gap-2">
          <Palette className="h-6 w-6 text-[#FF331F]" /> Appearance
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
          Each scheme shows a <span className="text-white font-bold">Dark</span> and <span className="text-white font-bold">Light</span> preview —
          click either thumbnail to apply that scheme in that mode. The website updates
          <span className="text-white font-bold"> instantly for all visitors</span>; the mobile app applies it on its
          <span className="text-white font-bold"> next launch</span>. This admin dashboard always stays dark Flow Red.
        </p>
      </header>

      {/* Quick Light / Dark mode toggle for the current scheme */}
      <div className="flex items-center gap-3" data-testid="mode-toggle">
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Mode</span>
        <div className="inline-flex rounded-md border border-[#27272A] bg-[#0A0A0A] p-1">
          <button
            type="button"
            onClick={() => setThemeMode("dark")}
            disabled={savingMode || loading}
            data-testid="mode-dark"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-bold transition-colors ${!isLight ? "bg-[#FF331F] text-white" : "text-zinc-400 hover:text-white"}`}
          >
            <Moon className="h-3.5 w-3.5" /> Dark
          </button>
          <button
            type="button"
            onClick={() => setThemeMode("light")}
            disabled={savingMode || loading}
            data-testid="mode-light"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-xs uppercase tracking-widest font-bold transition-colors ${isLight ? "bg-[#FF331F] text-white" : "text-zinc-400 hover:text-white"}`}
          >
            <Sun className="h-3.5 w-3.5" /> Light
          </button>
        </div>
        {savingMode && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading schemes…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {themes.map((t) => (
            <SchemeCard
              key={t.id}
              t={t}
              activeId={activeId}
              mode={mode}
              onApply={applyScheme}
              savingKey={savingKey}
            />
          ))}
        </div>
      )}
    </div>
  );
}
